// Card — the app's surface primitive (MASTER.md v3 "Dub" cards): white card on
// the near-white canvas, 1px --border hairline (THE separation device — no
// resting shadow), radius-card (12px), p-6 (24px) default padding via CardBody.
// Hover elevation is opt-in via `interactive` (border/surface shift, never a
// shadow). Renders a <div> by default; pass `as="article"` for semantic list
// items (job/company cards) or `as="section"` for page panels. The old
// hand-styled equivalent was "rounded-2xl bg-card p-6 dark:border
// dark:border-border" — under dub the hairline is the card's identity in both
// themes, so it is unconditional here.

import { createElement, forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/app/lib/cn";

export type CardElement = "div" | "section" | "article";

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Adds the dub hover treatment (border + surface shift) for clickable cards. */
  interactive?: boolean;
  as?: CardElement;
}

export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  { interactive = false, as = "div", className, children, ...rest },
  ref
) {
  // createElement (not JSX) because a polymorphic tag can't have one JSX ref
  // type across div/section/article; all three share HTMLElement at runtime.
  return createElement(
    as,
    {
      ref,
      className: cn(
        "rounded-card border border-border bg-card text-card-foreground",
        interactive &&
          "cursor-pointer transition-colors duration-150 hover:border-muted-foreground/40 hover:bg-muted/50",
        className
      ),
      ...rest,
    },
    children
  );
});

// Standard inner padding so card content stays on the 24px rhythm without
// hand-writing it per page. (MASTER.md: p-6 desktop; the pages step can
// override with className where a tighter layout is wanted.)
export function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("p-6", className)} {...rest}>
      {children}
    </div>
  );
}
